import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AboutManager = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/about`);
      setContent(response.data.content);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching about content:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/cms/about`, { content });
      toast({
        title: "Success",
        description: "About page updated successfully",
      });
    } catch (error) {
      console.error('Error saving about content:', error);
      toast({
        title: "Error",
        description: "Failed to update about page",
        variant: "destructive"
      });
    }
    setSaving(false);
  };

  const addValue = () => {
    setContent({
      ...content,
      values: [
        ...(content.values || []),
        { title: '', description: '', icon: 'Target' }
      ]
    });
  };

  const updateValue = (index, field, value) => {
    const newValues = [...content.values];
    newValues[index][field] = value;
    setContent({ ...content, values: newValues });
  };

  const removeValue = (index) => {
    const newValues = content.values.filter((_, i) => i !== index);
    setContent({ ...content, values: newValues });
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (!content) {
    return <div className="p-8">No content available</div>;
  }

  return (
    <div className="p-8 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-light text-[#2d2d2d]">About Page Manager</h1>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0A1A50] hover:bg-[#060F2E] text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Hero Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">Hero Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
            <Input
              value={content.hero_title || ''}
              onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
              placeholder="About Reagle"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Description</label>
            <Textarea
              value={content.hero_description || ''}
              onChange={(e) => setContent({ ...content, hero_description: e.target.value })}
              placeholder="Leading the way in innovative real estate development solutions"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">Mission Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
            <Input
              value={content.mission_title || ''}
              onChange={(e) => setContent({ ...content, mission_title: e.target.value })}
              placeholder="Our Mission"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Content</label>
            <Textarea
              value={content.mission_content || ''}
              onChange={(e) => setContent({ ...content, mission_content: e.target.value })}
              placeholder="To deliver exceptional real estate development services..."
              rows={4}
            />
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-light text-[#2d2d2d]">Values Section</h2>
          <Button
            onClick={addValue}
            variant="outline"
            className="text-[#0A1A50] border-[#0A1A50]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Value
          </Button>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Section Title</label>
            <Input
              value={content.values_title || ''}
              onChange={(e) => setContent({ ...content, values_title: e.target.value })}
              placeholder="Our Values"
            />
          </div>
        </div>

        <div className="space-y-6">
          {(content.values || []).map((value, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-normal text-[#2d2d2d]">Value {index + 1}</h3>
                <Button
                  onClick={() => removeValue(index)}
                  variant="destructive"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
                  <Input
                    value={value.title}
                    onChange={(e) => updateValue(index, 'title', e.target.value)}
                    placeholder="e.g., Integrity"
                  />
                </div>
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Description</label>
                  <Textarea
                    value={value.description}
                    onChange={(e) => updateValue(index, 'description', e.target.value)}
                    placeholder="Describe this value..."
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Icon</label>
                  <select
                    value={value.icon}
                    onChange={(e) => updateValue(index, 'icon', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="Target">Target (Bullseye)</option>
                    <option value="Award">Award (Trophy)</option>
                    <option value="TrendingUp">TrendingUp (Growth)</option>
                    <option value="Users">Users (People)</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">CTA Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">CTA Title</label>
            <Input
              value={content.cta_title || ''}
              onChange={(e) => setContent({ ...content, cta_title: e.target.value })}
              placeholder="Join us in creating exceptional real estate experiences"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutManager;
